import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

// Owner accounts are not treated as staff for this prompt
const DOB_EXEMPT_EMAILS = ["vinay@arborvitae.in", "support@arborvitae.in"];

export default function DobGate({ children }) {
  const { user } = useAuth();
  const [needsDob, setNeedsDob] = useState(false);
  const [dob, setDob] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const email = String(user?.email || "").toLowerCase();
    if (!user || DOB_EXEMPT_EMAILS.includes(email)) { setNeedsDob(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        if (!cancelled) setNeedsDob(!!data?.employee && !data.employee.dob);
      } catch (e) {
        if (!cancelled) setNeedsDob(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const save = async () => {
    if (!dob) { toast.error("Please enter your date of birth"); return; }
    const d = new Date(dob);
    if (Number.isNaN(d.getTime()) || d >= new Date()) { toast.error("Enter a valid date of birth"); return; }
    setSaving(true);
    try {
      await api.patch("/me/profile", { dob });
      toast.success("Thanks - date of birth saved");
      setNeedsDob(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {children}
      {needsDob && (
        <div className="fixed inset-0 z-[100] bg-zinc-900/60 flex items-center justify-center p-4" data-testid="dob-gate">
          <div className="bg-white rounded-xl border border-zinc-200 w-full max-w-md p-6">
            <h2 className="font-display text-xl font-bold text-zinc-900">One quick thing</h2>
            <p className="mt-2 text-sm text-zinc-600">
              We do not have your date of birth on record. Please add it to continue - you will only be asked once.
            </p>
            <div className="mt-5">
              <label htmlFor="dob-input" className="text-sm font-medium text-zinc-700">Date of birth</label>
              <input
                id="dob-input"
                type="date"
                value={dob}
                max={new Date().toISOString().slice(0, 10)}
                min="1940-01-01"
                onChange={(e) => setDob(e.target.value)}
                className="mt-1 w-full h-10 rounded-md border border-zinc-300 px-3 text-sm"
                data-testid="dob-input"
              />
            </div>
            <Button
              onClick={save}
              disabled={saving}
              className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 text-white h-10"
              data-testid="dob-save"
            >
              {saving ? "Saving..." : "Save and continue"}
            </Button>
            <p className="mt-3 text-xs text-zinc-500">
              Need it corrected later? Ask an admin - changes to date of birth need approval.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
